import type React from 'react';
import { useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { HTTPError } from 'ky';

export const GlobalErrorHandler: React.FC = () => {
  const { showError, showWarning, showInfo } = useToast();

  useEffect(() => {
    // 1. Handle API Errors via Custom Event
    const handleApiError = (event: ApiErrorEvent) => {
      const { status, message, url } = event.detail;

      console.log(
        `[GlobalErrorHandler] Handling API error ${status} from ${url}`
      );

      switch (status) {
        case 403:
          showError(
            "Accès refusé. Vous n'avez pas les permissions nécessaires."
          );
          break;
        case 404:
          // We might not want to show toasts for all 404s (some might be handled by UI)
          // But for API calls that fail, it's often useful
          showWarning('Ressource introuvable. Elle a peut-être été supprimée.');
          break;
        case 429:
          showWarning(
            'Trop de requêtes. Veuillez patienter quelques instants.'
          );
          break;
        case 503:
          showError(
            'Service momentanément indisponible. Veuillez réessayer plus tard.'
          );
          break;
        case 500:
        case 502:
        case 504:
          // Note: AuthContext might already handle some of these for checkAuth
          // But for other actions (saving forms), we need to tell the user
          if (!url?.includes('/auth/me')) {
            showError(
              'Erreur serveur. Veuillez réessayer ou contacter le support.'
            );
          }
          break;
        default:
          if (message) {
            showError(`Erreur: ${message}`);
          }
      }
    };

    // 2. Handle Network Status
    const handleOnline = () => {
      showInfo('Connexion rétablie.');
    };

    const handleOffline = () => {
      showWarning('Vous êtes hors ligne. Vérifiez votre connexion internet.');
    };

    // 3. Handle Unhandled Promise Rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[GlobalErrorHandler] Unhandled rejection:', event.reason);

      // Don't show toast for every random error to avoid spam,
      // but if it's an HTTPError that wasn't caught, we might want to
      if (event.reason instanceof HTTPError) {
        // These are usually handled by the api-error event if dispatched,
        // but if the promise chain was broken, we catch it here.
        return;
      }

      // Optional: Show generic error for critical failures?
      // showError('Une erreur inattendue est survenue.');
    };

    window.addEventListener('api-error', handleApiError);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('api-error', handleApiError);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, [showError, showWarning, showInfo]);

  return null; // This component is logical only, no UI render
};
